import type { IProductEntityFavorite } from '../models';
import type { IProductEntity } from '@/entities';

import { _FavoritesStore } from './FavoritesStore';
import { GoodsListClient } from '@/shared/api';
import { ErrorBus } from '@/features/error-bus';
import { snackBarManager } from '@/features/ui-kit';

import { useFavoritesTracker } from '@/pages/favorites/tracker/useFavoritesTracker';
import { useProductLite } from '@/shared/composables/useApollo';

const FavoriteStore = new _FavoritesStore();

FavoriteStore.setApiService({
  getFavorites: (page) => GoodsListClient.getGoodsList(page),
  addFavorite: (productId) => GoodsListClient.addToGoodsList(String(productId)),
  removeFavorite: (productId) => GoodsListClient.removeFromGoodsList(String(productId)),
  syncFavorites: () => GoodsListClient.syncFavorites(),
});

export function useFavorites() {
  const fetchNextFavoritesPage = async () => FavoriteStore.fetchNextFavoritesPage();
  const { fetchProductLite } = useProductLite();

  async function toggleFavorite(product: IProductEntityFavorite) {
    return product.isFavorite
      ? FavoriteStore.removeFavorite(product)
      : FavoriteStore.addFavorite(product);
  }

  async function buildFavoritesPayload(product: IProductEntity) {
    try {
      if (!product.id) return undefined;

      const { productLite } = await fetchProductLite(Number(product.id));
      if (!productLite) return undefined;

      return {
        product_id: Number(product.id),
        shop_id: productLite?.shop?.id,
        old_price: product.fullPrice,
        price: product.sellPrice,
        timestamp: Date.now(),
      };
    } catch {
      return undefined;
    }
  }

  async function sendFavoriteAnalytics(product: IProductEntity, place: string, extraParams: Record<string, any> = {}, wasFavorite: boolean) {
    try {
      const { 
        trackAddToFavorites, 
        trackRemoveFromFavorites,
        trackAddToFavoritesProductScreen,
        trackRemoveFromFavoritesProductScreen
      } = useFavoritesTracker({ block: place });

      const basePayload = await buildFavoritesPayload(product);
      
      if (basePayload) {
        const fullPayload = { ...basePayload, ...extraParams };
        
        if (place === 'productScreen') {
          wasFavorite 
            ? trackRemoveFromFavoritesProductScreen(fullPayload) 
            : trackAddToFavoritesProductScreen(fullPayload);
        } else {
          wasFavorite 
            ? trackRemoveFromFavorites(fullPayload) 
            : trackAddToFavorites(fullPayload);
        }
      }
    } catch (analyticsError) {
      console.warn('Analytics error for favorite toggle:', analyticsError);
    }
  }

  const handleFavoriteToggle = async (product: IProductEntity, place: string = 'Unknown', extraParams: Record<string, any> = {}) => {
    if (!product.id) {
      return;
    }

    const previousState = product.isFavorite;

    try {
      await toggleFavorite(product as IProductEntityFavorite);
    } catch (error) {
      ErrorBus.emitError({
        e: error as Error,
        emitter: `FavoriteHandler.${place}`,
        payload: {
          productId: product.id,
          productTitle: product.title,
          context: place
        }
      });

      snackBarManager.showSnackBar({
        type: 'error',
        message: 'Не удалось обновить избранное. Попробуйте еще раз',
        dismissible: true,
        duration: 5000,
      });

      if (product.isFavorite !== undefined) {
        product.isFavorite = previousState;
      }

      return;
    }

    await sendFavoriteAnalytics(product, place, extraParams, previousState);
  };

  const handleFavoriteToggleCurried = (place: string) => (product: IProductEntity, extraParams: Record<string, any> = {}) => {
    return handleFavoriteToggle(product, place, extraParams);
  };

  const resetFavoritesList = () => FavoriteStore.reset();

  const syncFavorites = () => FavoriteStore.syncFavorites().then(() => {resetFavoritesList()}).then(() => FavoriteStore.fetchFavorites(1));

  return {
    fetchNextFavoritesPage,
    handleFavoriteToggle,
    handleFavoriteToggleCurried,
    resetFavoritesList,
    syncFavorites,

    favoriteProducts: FavoriteStore.favoriteProducts,
    canFetchMore: FavoriteStore.canFetchMore,
    totalCount: FavoriteStore.totalCount,
  };
}
